import {ReactNode} from 'react';

import {
  FeatureFlags,
  FeatureFlagsProvider,
  useWidgetTriggerParams,
} from '@stripe-internal/conversations-ui';
import {ViewMode} from '@stripe-internal/support-conversations-controller';

import type {AppConfig, AppProfile} from '../../core/types';
import {
  isCheckoutStudioTriggerPoint,
  isDotComCsfSelfServeRedirectTriggerPoint,
} from '../../core/lib/util/triggerPoints';
import {
  isDotComEntryPoint,
  isMobileEntryPoint,
} from '../../core/lib/util/conversationUtils';
import {
  useAudience,
  useEntryPoint,
  useFlags,
  useTriggerParams,
  useViewMode,
} from '../../core/hooks/appHooks';

const conversationsUIViewModeByAppViewMode = {
  [ViewMode.SupportWidget]: 'support-widget',
  [ViewMode.CSATSurvey]: 'csat-survey',
  [ViewMode.Drawer]: 'drawer',
  [ViewMode.Minimal]: 'minimal',
} satisfies Record<ViewMode, FeatureFlags['viewMode']>;

type ConversationsUIFeatureFlagsProviderProps = {
  children: ReactNode;
  featureFlags: AppProfile['featureFlags'];
  audience: AppConfig['audience'];
  entryPoint: AppConfig['entryPoint'];
  triggerPoint?: string;
  viewMode: ViewMode;
};

export function ConversationsUIFeatureFlagsProvider({
  children,
  featureFlags,
  audience,
  entryPoint,
  triggerPoint,
  viewMode,
}: ConversationsUIFeatureFlagsProviderProps) {
  const {triggerParams: widgetTriggerParams} = useWidgetTriggerParams();
  const {
    enableUniversalHelp,
    universalHelpUseNewDesign,
    universalHelpDotComFeedbackButtons,
    universalHelpDotComNoLlm,
    universalHelpDotComCsf,
    universalHelpUseInlineRacEmailForm,
    universalHelpEncourageMoreDetailFromUserMse5042,
    enableScreenshareUi,
    enableUhPendingInput,
  } = featureFlags;

  const value: FeatureFlags = {
    newUniversalHelpDesign: enableUniversalHelp && universalHelpUseNewDesign,
    universalHelpDotComDesign:
      enableUniversalHelp &&
      (audience === 'dot_com' || isDotComEntryPoint(entryPoint)),
    dotComFeedbackButtons:
      enableUniversalHelp && universalHelpDotComFeedbackButtons,
    dotComNoLlm: enableUniversalHelp && universalHelpDotComNoLlm,
    dotComCsf: enableUniversalHelp && universalHelpDotComCsf,
    dotComCsfSelfServe: isDotComCsfSelfServeRedirectTriggerPoint(
      widgetTriggerParams?.triggerPoint,
    ),
    messagesTopAnchored: enableUniversalHelp && !isMobileEntryPoint(entryPoint),
    inlineRacEmailForm:
      enableUniversalHelp && universalHelpUseInlineRacEmailForm,
    conversationsLiveAnswersRow: true,
    universalHelpEncourageMoreDetailFromUserMse5042:
      universalHelpEncourageMoreDetailFromUserMse5042 &&
      !isCheckoutStudioTriggerPoint(triggerPoint),
    enableScreenshareUi,
    enableUhPendingInput,
    viewMode: conversationsUIViewModeByAppViewMode[viewMode],
  };

  return <FeatureFlagsProvider value={value}>{children}</FeatureFlagsProvider>;
}

export function ConversationsUIFlagsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const featureFlags = useFlags();
  const audience = useAudience();
  const entryPoint = useEntryPoint();
  const triggerParams = useTriggerParams();
  const viewMode = useViewMode();

  return (
    <ConversationsUIFeatureFlagsProvider
      featureFlags={featureFlags}
      audience={audience}
      entryPoint={entryPoint}
      triggerPoint={triggerParams?.triggerPoint}
      viewMode={viewMode}
    >
      {children}
    </ConversationsUIFeatureFlagsProvider>
  );
}
